import type { MorningApplication, MorningSearchShortcut } from '../types/morning'

type KeyboardLike = {
  key: string
  metaKey?: boolean
  ctrlKey?: boolean
  altKey?: boolean
  target?: unknown
}

const spotlightExcludedRoutes = new Set(['/login', '/setup', '/tenant-select', '/tenant-selector'])

export function isUserSpotlightRoute(path: string) {
  return !spotlightExcludedRoutes.has(path)
}

export function isApplePlatform(platform: string, userAgent = '') {
  return /Mac|iPhone|iPad|iPod/i.test(`${platform} ${userAgent}`)
}

export function spotlightShortcutHint(shortcut: MorningSearchShortcut, apple: boolean) {
  if (shortcut === 'none') return null
  if (shortcut === 'slash') return '/'
  return apple ? '⌘ K' : 'Ctrl K'
}

export function isEditableTarget(target: unknown) {
  if (!target || typeof target !== 'object') return false
  const candidate = target as { tagName?: string; isContentEditable?: boolean }
  return (
    Boolean(candidate.isContentEditable) ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(candidate.tagName?.toUpperCase() ?? '')
  )
}

export function matchesSpotlightShortcut(
  event: KeyboardLike,
  shortcut: MorningSearchShortcut,
  apple: boolean,
) {
  if (shortcut === 'none') return false
  if (shortcut === 'slash') {
    return (
      event.key === '/' &&
      !event.metaKey &&
      !event.ctrlKey &&
      !event.altKey &&
      !isEditableTarget(event.target)
    )
  }
  return (
    event.key.toLocaleLowerCase() === 'k' &&
    (apple ? Boolean(event.metaKey) : Boolean(event.ctrlKey))
  )
}

export function applicationOpenTarget(application: MorningApplication, forceNewTab: boolean) {
  return forceNewTab ? ('_blank' as const) : application.target
}

export function openSpotlightApplication(
  application: MorningApplication,
  forceNewTab: boolean,
  browser: {
    open: (url: string, target: string, features: string) => unknown
    assign: (url: string) => unknown
  },
) {
  if (applicationOpenTarget(application, forceNewTab) === '_blank') {
    browser.open(application.url, '_blank', 'noopener,noreferrer')
  } else {
    browser.assign(application.url)
  }
}
